import {some, find, filter, forEach, has, includes, map} from 'lodash';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSound} from 'use-sound';

import {groupApi, sessionApi, userSettingsApi} from 'api';
import {
    MediaPlayCheck, PageHeader, Icon, notifier, Toggle, Button, Loader,
    ScreenRecorderStatus, AccountPlanBanner, ShowMoreItemsContainer, MediaDevicesDialog,
    IbmMessageHistoryModal, IconName, CallButton, CallButtonProps, Tooltip
} from 'components';
import {AGENT_ACTIVE_SESSION_STATUSES, AGENT_CAN_ANSWER_SESSION_STATUSES} from 'constants/session';
import {themeConfig} from 'constants/theme';
import {NOT_ALLOWED_ERROR} from 'constants/webrtc';
import {AuthLayoutRouteContext, UserContext} from 'context';
import {OnlineSessionsViewType} from 'enums';
import {useApiData, useSessionReadyState, useObservable, useMediaDevices, useUserInfoSchema} from 'hooks';
import {Group, OnlineSession, SessionStatus, OnlineSessionsStore, AccountAssignmentInfo, SessionActivityName} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {onlineSessionsStore, onlineSessionsViewTypeStore, getAssignmentName, authDataStore} from 'services';
import {mediaDevicesUtils, sessionUtils} from 'utils';

import {ActivityDataModal} from './ActivityDataModal';
import {ParticipantModal} from './ParticipantsModal';
import {PrevClientSessionsModal} from './PrevClientSessionsModal';
import {SessionStatusChips} from './SessionStatusChips';
import {SessionsGrid} from './SessionsGrid';
import {SessionsTable} from './SessionsTable';

const T_KEYS = TRANSLATION_KEYS.ONLINE_SESSIONS_PAGE;
const CLIENT_STATUS_KEYS = TRANSLATION_KEYS.ONLINE_SESSIONS_PAGE.TABLE.CLIENT_STATUS;
const NOTIFIER_KEYS = TRANSLATION_KEYS.UI_KIT.NOTIFIER;

const RING_SOUND_URL = '/assets/sounds/ring.mp3';

export enum SessionStatusFilter {
    TOTAL,
    CALLING,
    IN_SESSION
}
export type SessionStatusMap = {[key: string]: number};

export const Sessions: React.FC = () => {
    const {t} = useTranslation();
    const {settings, reloadSettings} = useContext(UserContext);
    const {localUser} = useObservable(authDataStore);
    const {setAuthLayoutProps} = useContext(AuthLayoutRouteContext);

    const [sessions, setSessions] = useState<OnlineSession[]>([]);
    const [sessionStatusMap, setSessionStatusMap] = useState<SessionStatusMap>({TOTAL: 0, CALLING: 0, IN_SESSION: 0});
    const [selectedSessionStatusFilter, setSelectedSessionStatusFilter] = useState<SessionStatusFilter>(SessionStatusFilter.TOTAL);
    const [isLoadClientsError, setIsLoadClientsError] = useState<boolean>(false);
    const [sessionsLoaded, setSessionsLoaded] = useState<boolean>(false);
    const [isIncomingSessionSoundEnabledLocal, setIsIncomingSessionSoundEnabledLocal] = useState<boolean | null>(null);
    const [groupsLoaded, setGroupsLoaded] = useState<boolean>(false);
    const [prevClientSessionsModalProps, setPrevClientSessionsModalProps] = useState<{
        open: boolean, session?: OnlineSession}>({open: false});
    const [participantModalProps, setParticipantModalProps] = useState<{
            open: boolean, sessionId?: string}>({open: false});
    const [activityDataModalProps, setActivityDataModalProps] = useState<{
        open: boolean; session?: OnlineSession, activityName?: SessionActivityName}>({open: false});
    const [ibmMessagesModalProps, setIbmMessagesModalProps] = useState<{open: boolean, sessionHistoryKey?: string}>({open: false});
    const [viewType, setViewType] = useState<OnlineSessionsViewType>(OnlineSessionsViewType.Table);
    const [mediaDevicesDialogOpen, setMediaDevicesDialogOpen] = useState(false);
    const [mediaDevices, setMediaDevices] = useMediaDevices();

    useUserInfoSchema({fetchOnInit: true, guestEnabled: settings.featuresAvailability.guestEnabled});

    const isIncomingSessionSoundEnabled = useMemo(() => isIncomingSessionSoundEnabledLocal === null
        ? settings.common?.incomingSessionSound?.enabled
        : isIncomingSessionSoundEnabledLocal, [settings, isIncomingSessionSoundEnabledLocal]);

    const callingSoundEnabled = useMemo(() =>
        isIncomingSessionSoundEnabled && some(sessions, {status: SessionStatus.Calling}), [sessions, isIncomingSessionSoundEnabled]);

    const [playCallingSound, {stop: stopCallingSound}] = useSound(RING_SOUND_URL, {loop: true, interrupt: true});

    const clientInvitationEnabled = useMemo(() => settings.clientInvitation?.enabled, [settings]);

    const timeZone = useMemo(() => settings.common?.timeZone, [settings]);

    const isLoading = useMemo(() => !sessionsLoaded || !groupsLoaded, [sessionsLoaded, groupsLoaded]);

    const {canJoin, activeSessionId} = useSessionReadyState();

    const initViewType = useCallback(() => {
        const storedViewType = onlineSessionsViewTypeStore.getValue();
        if (!storedViewType) {
            const windowWidth = window?.innerWidth;
            const newViewType = windowWidth >= themeConfig.screens.lg ? OnlineSessionsViewType.Table : OnlineSessionsViewType.Grid;
            onlineSessionsViewTypeStore.setValue(newViewType);
        }
    }, []);

    const answerClient = useCallback(async (sessionId: string) => {
        if (!canJoin) {
            return;
        }
        try {
            const session = find(sessions, {id: sessionId});
            await sessionUtils.joinSession(session, settings.featuresAvailability.assistantEnabled);
        } catch {
            notifier.error({title: t(T_KEYS.ERROR.JOIN_SESSION_FAILED)});
        }
    }, [canJoin, sessions, settings.featuresAvailability.assistantEnabled, t]);

    const joinActiveSession = useCallback(() => {
        if (!activeSessionId) {
            return;
        }
        sessionUtils.goToSession();
    }, [activeSessionId]);

    const onSessionsUpdated = useCallback((data: OnlineSessionsStore) => {
        const filteredSessions = filter(data.sessions, (session) =>
            !includes(map(session.statuses, (s) => s.name), SessionStatus.AppointmentNotStarted));

        const newSessionStatistic: SessionStatusMap = {
            TOTAL: filteredSessions.length,
            CALLING: 0,
            IN_SESSION: 0
        };

        forEach(filteredSessions, ({status}) => {
            if (has(newSessionStatistic, status)) {
                newSessionStatistic[status] += 1;
            }
        });

        setSessionStatusMap(newSessionStatistic);
        setSessions(filteredSessions);
        setIsLoadClientsError(Boolean(data.error));
        setSessionsLoaded(data.sessionsLoaded);
    }, []);

    const setSoundEnable = useCallback(async (isSoundEnabled: boolean) => {
        setIsIncomingSessionSoundEnabledLocal(isSoundEnabled);
        try {
            await userSettingsApi.updateIncomingMessageSoundSettings({value: isSoundEnabled});
            await reloadSettings?.();
        } catch {
            notifier.error({title: t(NOTIFIER_KEYS.PREDEFINED.SAVE.ERROR)});
        }
    }, [reloadSettings, t]);

    const onSoundCheckError = useCallback((error: Error): void => {
        let message = error.message;
        if (error.name === NOT_ALLOWED_ERROR) {
            message = t(T_KEYS.ERROR.AUDIO_NOT_ALLOWED);
        }

        notifier.error({title: message});
        setSoundEnable(false);
    }, [setSoundEnable, t]);

    const startSession = useCallback(async () => {
        try {
            await sessionApi.createSession({autoStart: true});
            sessionUtils.goToSession();
        } catch {
            notifier.error({title: t(T_KEYS.ERROR.CREATE_SESSION_FAILED)});
        }
    }, [t]);

    const openPrevClientSessionsModal = useCallback((session: OnlineSession) => {
        setPrevClientSessionsModalProps({open: true, session});
    }, []);

    const openParticipantsModal = useCallback((sessionId: string) => {
        setParticipantModalProps({open: true, sessionId});
    }, []);

    const openActivityDataModal = useCallback((session: OnlineSession, activityName: SessionActivityName) => {
        setActivityDataModalProps({open: true, activityName, session});
    }, []);

    const closePrevClientSessionsModal = useCallback(() => {
        setPrevClientSessionsModalProps({open: false});
    }, []);

    const closeParticipantModal = useCallback(() => {
        setParticipantModalProps({open: false});
    }, []);

    const closeActivityDataModal = useCallback((params?: {ibm?: {sessionHistoryKey: string}}) => {
        setActivityDataModalProps({open: false});
        if (params?.ibm?.sessionHistoryKey) {
            setIbmMessagesModalProps({open: true, sessionHistoryKey: params.ibm.sessionHistoryKey});
        }
    }, []);

    const closeIbmMessagesModal = useCallback(() => {
        setIbmMessagesModalProps({open: false});
    }, []);

    const handleSessionAnswer = useCallback((session: OnlineSession) => {
        if (session.status === SessionStatus.New || sessionUtils.isUserAssistantInSession(session, localUser.eid)) {
            return sessionUtils.goToJoinSessionPage(session);
        }
        answerClient(session.id);
    }, [answerClient, localUser]);

    const changeViewType = useCallback((vt: OnlineSessionsViewType) => onlineSessionsViewTypeStore.setValue(vt), []);

    const getStatusOptions = useCallback((session: OnlineSession) => {
        const sessionStatus = sessionUtils.getSessionStatus(session);
        const status = {
            name: t(CLIENT_STATUS_KEYS[sessionStatus])?.toLocaleUpperCase(),
            className: 'tw-text-default-dimmed'
        };

        if (sessionStatus === SessionStatus.Calling) {
            status.className = 'tw-text-alert';
        } else if (sessionStatus === SessionStatus.InSession) {
            status.className = 'tw-text-success';
        }

        return status;
    }, [t]);

    const onGroupFinally = useCallback(() => {
        setGroupsLoaded(true);
    }, []);

    const {send: getGroups, data: groups = []} = useApiData<Group[]>(groupApi.getGroups, {
        onFinally: onGroupFinally
    });

    const renderAssignment = useCallback(({groupId, subgroupId}: AccountAssignmentInfo) =>
        getAssignmentName(groups, groupId, subgroupId), [groups]);

    const renderPreSessionFlowBadge = useCallback((session: OnlineSession) => {
        const summary = session.preSessionFlow;
        if (!summary || summary.totalSteps === 0) {
            return null;
        }
        const isComplete = summary.doneSteps >= summary.totalSteps || summary.awaitingConfirmation;
        let color: 'alert' | 'success' = 'alert';
        let customClass = '';
        if (isComplete) {
            color = 'success';
        } else if (summary.doneSteps > 0) {
            customClass = '!tw-text-warning';
        }
        const tooltipMessage = t(T_KEYS.PRE_SESSION_FLOW_BADGE.TOOLTIP, {
            done: summary.doneSteps,
            total: summary.totalSteps,
        });
        return (
            <Tooltip message={tooltipMessage} style={{display: 'inline-flex'}}>
                <Icon
                    name='checklist'
                    color={color}
                    className={customClass}
                    size='sm'
                    margin={false}
                    thickness='md'
                />
            </Tooltip>
        );
    }, [t]);

    const renderActivityDataIcon = useCallback((session: OnlineSession) => {
        const hasTextChatActivity = includes(session.activities, SessionActivityName.TextChat);
        const hasSipActivity = includes(session.activities, SessionActivityName.SIP);
        const hasAvatarActivity = includes(session.activities, SessionActivityName.Avatar);
        const isVisible = hasSipActivity || hasTextChatActivity || hasAvatarActivity;
        let activityName = null;
        if (hasTextChatActivity) {
            activityName = SessionActivityName.TextChat;
        } else if (hasSipActivity) {
            activityName = SessionActivityName.SIP;
        } else if (hasAvatarActivity) {
            activityName = SessionActivityName.Avatar;
        } else {
            return null;
        }

        let icon: IconName;
        if (hasTextChatActivity) {
            icon = 'text-chat-history';
        } else if (hasSipActivity) {
            icon = 'phone-history';
        } else if (hasAvatarActivity) {
            icon = 'avatar-history';
        }

        return isVisible && (
            <div
                className='tw-flex tw-items-center tw-cursor-pointer'
                onClick={(e) => {
                    e.stopPropagation();
                    openActivityDataModal(session, activityName);
                }}
            >
                <Icon
                    button
                    name={icon}
                    color='primary'
                    size='sm'
                    margin={false}
                    thickness='sm'
                />
            </div>
        );
    }, [openActivityDataModal]);

    const canJoinSession = useCallback((session: OnlineSession) =>
        sessionUtils.canJoinSession(session, settings.featuresAvailability.assistantEnabled),
    [settings.featuresAvailability.assistantEnabled]);

    const getAnswerDisabled = useCallback((session: OnlineSession) =>
        !localUser.isAvailable || !canJoin || !canJoinSession(session), [localUser.isAvailable, canJoin, canJoinSession]);

    const renderAnswerButton = useCallback((
        session: OnlineSession, buttonProps?: CallButtonProps, defaultNode: React.ReactNode = null
    ) => {
        if (canJoinSession(session) && (
            AGENT_CAN_ANSWER_SESSION_STATUSES.includes(session.status) ||
            AGENT_ACTIVE_SESSION_STATUSES.includes(session.status) && sessionUtils.isUserAssistantInSession(session, localUser.eid)
        )) {
            const hasActiveSession = !!activeSessionId;
            const answerDisabledForSession = hasActiveSession || getAnswerDisabled(session);
            return (
                <CallButton
                    animated={!answerDisabledForSession}
                    disabled={answerDisabledForSession}
                    onClick={() => handleSessionAnswer(session)}
                    {...buttonProps}
                />
            );
        }
        return defaultNode;
    }, [activeSessionId, localUser, getAnswerDisabled, handleSessionAnswer, canJoinSession]);

    const renderSessionButton = useCallback(() => {
        if (!sessionUtils.canManageSession(localUser, settings.featuresAvailability.assistantEnabled)) {
            return null;
        } else if (activeSessionId) {
            return (
                <Button color='success' rounded className='tw-animate-pulse' onClick={joinActiveSession}>
                    <span className='-md:tw-hidden'>
                        {t(T_KEYS.BUTTONS.JOIN_ACTIVE_SESSION)}
                    </span>
                    <span className='md:tw-hidden'>
                        {t(T_KEYS.BUTTONS.JOIN)}
                    </span>
                </Button>
            );
        } else if (clientInvitationEnabled) {
            return (
                <Button
                    color='primary-dimmed'
                    Icon={<Icon name='video-call' color='light' className='tw-mr-2 tw--mb-1' />}
                    rounded
                    disabled={!canJoin}
                    testAttribute={{id: 'start-session-button'}}
                    onClick={startSession}
                >
                    <span className='-md:tw-hidden'>
                        {t(T_KEYS.BUTTONS.NEW_SESSION)}
                    </span>
                    <span className='md:tw-hidden'>
                        {t(T_KEYS.BUTTONS.NEW)}
                    </span>
                </Button>
            );
        }
        return null;

    }, [
        localUser, settings.featuresAvailability.assistantEnabled, activeSessionId, clientInvitationEnabled,
        joinActiveSession, t, canJoin, startSession
    ]);

    const renderDynamicHeader = useCallback(() => (
        <div className='tw-flex tw-items-center tw-justify-end'>
            {settings.userSession?.requireScreenRecorder && <ScreenRecorderStatus />}
            <div className='-sm:tw-hidden'>
                {renderSessionButton()}
            </div>
        </div>
    ), [settings, renderSessionButton]);

    const filteredSessions = useMemo(() => {
        if (selectedSessionStatusFilter === SessionStatusFilter.IN_SESSION) {
            return filter(sessions, ({status: SessionStatus.InSession}));
        } else if (selectedSessionStatusFilter === SessionStatusFilter.CALLING) {
            return filter(sessions, ({status: SessionStatus.Calling}));
        }
        return sessions;
    }, [sessions, selectedSessionStatusFilter]);

    const selectedSession = useMemo(() => {
        if (sessions && participantModalProps) {
            return find(sessions, ({id}) => id === participantModalProps.sessionId);
        }
        return null;
    }, [sessions, participantModalProps]);

    useEffect(() => {
        initViewType();
        getGroups();
    }, [initViewType, getGroups]);

    useEffect(() => onlineSessionsViewTypeStore.subscribe((vt) => setViewType(vt)), []);

    useEffect(() => onlineSessionsStore.subscribe(onSessionsUpdated), [onSessionsUpdated]);

    useEffect(() => {
        if (callingSoundEnabled) {
            playCallingSound();
        } else {
            stopCallingSound();
        }
    }, [callingSoundEnabled, playCallingSound, stopCallingSound]);

    useEffect(() => () => {
        stopCallingSound();
    }, [stopCallingSound]);

    useEffect(() => {
        setAuthLayoutProps({dynamicHeader: renderDynamicHeader()});
    }, [renderDynamicHeader, setAuthLayoutProps]);

    return (
        <Fragment>
            <PageHeader title={t(TRANSLATION_KEYS.USER.PAGE_NAMES.ONLINE.SESSIONS)} />
            <AccountPlanBanner className='tw-mb-10' />
            {!isLoadClientsError && isLoading &&
                <div className='tw-relative tw-h-10'><Loader className='tw-max-h-12 tw-inset-0 tw-mt-2' /></div>}
            {!isLoadClientsError && !isLoading &&
                <Fragment>
                    <div className='tw-flex tw-justify-between tw-flex-wrap'>
                        <SessionStatusChips
                            sessionStatusMap={sessionStatusMap}
                            selectedSessionStatusFilter={selectedSessionStatusFilter}
                            setSelectedSessionStatusFilter={setSelectedSessionStatusFilter}
                        />
                        <div className='-sm:tw-w-full tw-flex tw-items-center tw-justify-end tw-mt-4 tw-mb-2'>
                            <div className='sm:tw-hidden tw-mr-auto'>{renderSessionButton()}</div>
                            <Icon
                                name='view-grid'
                                button
                                group
                                color={viewType === OnlineSessionsViewType.Grid ? 'primary' : 'default-dimmed'}
                                onClick={() => changeViewType(OnlineSessionsViewType.Grid)}
                            />
                            <Icon
                                name='view-list'
                                button
                                group
                                color={viewType === OnlineSessionsViewType.Table ? 'primary' : 'default-dimmed'}
                                className='tw-ml-2'
                                onClick={() => changeViewType(OnlineSessionsViewType.Table)}
                            />
                            {mediaDevicesUtils.canChangeMediaSettings() && <Icon
                                name='media-settings'
                                button
                                group
                                className='tw-ml-2'
                                onClick={() => setMediaDevicesDialogOpen(true)}
                            />}
                            <Icon
                                name={isIncomingSessionSoundEnabled ? 'bell-on' : 'bell-off'}
                                button
                                group
                                primary={isIncomingSessionSoundEnabled}
                                className='sm:tw-hidden tw-ml-2'
                                onClick={() => setSoundEnable(!isIncomingSessionSoundEnabled)}
                            />
                            <Toggle
                                name='ring-sound'
                                label={t(T_KEYS.SOUND_LABEL)}
                                labelPlacement='right'
                                className='-sm:tw-hidden'
                                checked={isIncomingSessionSoundEnabled}
                                onChange={() => setSoundEnable(!isIncomingSessionSoundEnabled)}
                            />
                        </div>
                    </div>

                    <div className='-sm:tw-mb-10'>
                        <ShowMoreItemsContainer items={filteredSessions} showMoreBtnClassName='tw-my-4'>
                            {({items}) => (
                                viewType === OnlineSessionsViewType.Grid ?
                                    <SessionsGrid
                                        sessions={items}
                                        getStatusOptions={getStatusOptions}
                                        renderAssignment={renderAssignment}
                                        openParticipantsModal={openParticipantsModal}
                                        renderActivityDataIcon={renderActivityDataIcon}
                                        renderPreSessionFlowBadge={renderPreSessionFlowBadge}
                                        renderAnswerButton={renderAnswerButton}
                                        onOpenPrevClientSessionsModal={openPrevClientSessionsModal}
                                    /> : <SessionsTable
                                        sessions={items}
                                        guestEnabled={settings.featuresAvailability.guestEnabled}
                                        getStatusOptions={getStatusOptions}
                                        renderAssignment={renderAssignment}
                                        openParticipantsModal={openParticipantsModal}
                                        renderActivityDataIcon={renderActivityDataIcon}
                                        renderPreSessionFlowBadge={renderPreSessionFlowBadge}
                                        renderAnswerButton={renderAnswerButton}
                                        onOpenPrevClientSessionsModal={openPrevClientSessionsModal}
                                    />
                            )}
                        </ShowMoreItemsContainer>
                    </div>

                    {isIncomingSessionSoundEnabled &&
                        <MediaPlayCheck onError={(e) => onSoundCheckError(e)} />}
                </Fragment>}

            {isLoadClientsError &&
                <div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12'>
                    <Icon name='alert-circle' size='xl' color='alert' />
                    <p className='tw-mt-3 tw-text-base tw-text-alert'>{t(T_KEYS.LOAD_ERROR.MESSAGE)}</p>
                    <p className='tw-text-sm tw-text-default-dimmed'>{t(T_KEYS.LOAD_ERROR.RELOAD_PAGE_MESSAGE)}</p>
                </div>}

            <PrevClientSessionsModal
                {...prevClientSessionsModalProps}
                timeZone={timeZone}
                groups={groups}
                onClose={closePrevClientSessionsModal}
            />

            <ActivityDataModal {...activityDataModalProps} onClose={closeActivityDataModal} />

            <IbmMessageHistoryModal {...ibmMessagesModalProps} onClose={closeIbmMessagesModal} />

            <ParticipantModal
                open={participantModalProps.open}
                session={selectedSession}
                onClose={closeParticipantModal}
            />
            {mediaDevicesUtils.canChangeMediaSettings() &&
                <MediaDevicesDialog
                    open={mediaDevicesDialogOpen}
                    mediaDevices={mediaDevices}
                    onClose={() => setMediaDevicesDialogOpen(false)}
                    onChange={setMediaDevices}
                />}
        </Fragment>
    );
};
